import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { QuestionnaireQuestion, QuestionnaireSection } from '@/types/generated'

export interface SectionInfo {
  section: QuestionnaireSection
  sectionIndex: number
  startFieldIndex: number
  endFieldIndex: number
  isRepeatable: boolean
  participantType: string | null
}

export interface FieldSectionMapping {
  fieldUuid: string
  sectionUuid: string
  sectionInfo: SectionInfo
  isLastFieldInSection: boolean
}

export const useQuestionnaireStore = defineStore('questionnaireStore', () => {
  const formData = ref({} as Record<string, any>)
  const activeFieldId = ref('')
  const activeFieldIndex = ref(0)
  const flatFields = ref<QuestionnaireQuestion[]>([])
  const rawSchema = ref<QuestionnaireSection[]>([])
  const fieldSectionMap = ref<Map<string, FieldSectionMapping>>(new Map())

  // Navigation history stack to support back navigation with logic jumps
  const navigationHistory = ref<number[]>([])

  // State for showing the repeat section prompt
  const showRepeatSectionPrompt = ref(false)
  const pendingRepeatSectionInfo = ref<SectionInfo | null>(null)

  // Computed properties
  const formLength = computed(() => flatFields.value.length)
  const isLastField = computed(() => activeFieldIndex.value === flatFields.value.length)
  const isFirstField = computed(() => activeFieldIndex.value === 0)

  function updateFormData(fieldId: string, value: any) {
    formData.value[fieldId] = value
  }

  function setFormData(data: Record<string, any>) {
    formData.value = data
  }

  function initializeForm(schema: QuestionnaireSection[]) {
    rawSchema.value = schema
    flatFields.value = []
    fieldSectionMap.value = new Map()

    let fieldIndex = 0

    if (Array.isArray(schema)) {
      schema.forEach((section, sectionIndex) => {
        const sectionStartIndex = fieldIndex
        const isRepeatable = (section as any).repeatable === true
        const participantType = (section as any).participant_type || null

        if (Array.isArray(section.items)) {
          const sectionFieldCount = section.items.length

          section.items.forEach((field, fieldIndexInSection) => {
            flatFields.value.push(field as QuestionnaireQuestion)
            if (field.value !== undefined && field.value !== '') {
              updateFormData(field.uuid, field.value)
            }

            const sectionInfo: SectionInfo = {
              section,
              sectionIndex,
              startFieldIndex: sectionStartIndex,
              endFieldIndex: sectionStartIndex + sectionFieldCount - 1,
              isRepeatable,
              participantType
            }

            const mapping: FieldSectionMapping = {
              fieldUuid: field.uuid,
              sectionUuid: section.uuid,
              sectionInfo,
              isLastFieldInSection: fieldIndexInSection === sectionFieldCount - 1
            }

            fieldSectionMap.value.set(field.uuid, mapping)
            fieldIndex++
          })
        }
      })
    }

    // Set initial active field
    let initialActiveField = flatFields.value[0] || {}
    for (let i = 0; i < flatFields.value.length; i++) {
      if (
        formData.value[flatFields.value[i].uuid] === undefined ||
        formData.value[flatFields.value[i].uuid] === '' ||
        formData.value[flatFields.value[i].uuid] === null
      ) {
        initialActiveField = flatFields.value[i]
        break
      }
    }

    const initialActiveIndex = flatFields.value.findIndex(
      (field) => field.uuid === initialActiveField.uuid
    )
    activeFieldIndex.value = initialActiveIndex !== -1 ? initialActiveIndex : 0
    activeFieldId.value = initialActiveField.uuid || ''
    navigationHistory.value = []
  }

  function jumpToField(uuid: string) {
    // Reset repeat section prompt when jumping to a different field (e.g., from sidebar)
    setRepeatSectionPrompt(false)

    const index = flatFields.value.findIndex((field) => field.uuid === uuid)
    if (index !== -1) {
      navigationHistory.value.push(activeFieldIndex.value)
      activeFieldIndex.value = index
      activeFieldId.value = uuid
    }
  }

  function goToNextField() {
    navigationHistory.value.push(activeFieldIndex.value)
    const nextIndex = findNextField(activeFieldIndex.value)
    activeFieldIndex.value = nextIndex
    if (!isLastField.value) {
      activeFieldId.value = flatFields.value[nextIndex]?.uuid || ''
    }
  }

  function goToPreviousField() {
    // Reset repeat section prompt when going back
    setRepeatSectionPrompt(false)

    if (navigationHistory.value.length > 0) {
      const previousIndex = navigationHistory.value.pop()!
      activeFieldIndex.value = previousIndex
      activeFieldId.value = flatFields.value[previousIndex]?.uuid || ''
    } else if (activeFieldIndex.value > 0) {
      activeFieldIndex.value--
      activeFieldId.value = flatFields.value[activeFieldIndex.value]?.uuid || ''
    }
  }

  function setRepeatSectionPrompt(show: boolean, sectionInfo: SectionInfo | null = null) {
    showRepeatSectionPrompt.value = show
    pendingRepeatSectionInfo.value = sectionInfo
  }

  function findNextField(currentIndex: number): number {
    const currentField = flatFields.value[currentIndex]

    if (currentField?.jump_to && currentField.jump_to.length > 0) {
      const jumpCondition = currentField.jump_to.find((jump) => {
        if (currentField.type === 'yesno') {
          const formDataValue = formData.value[currentField.uuid]
          const jumpValueLower = jump.value.toLowerCase()
          return (
            ((jumpValueLower === 'yes' || jumpValueLower === 'ja') && formDataValue === true) ||
            ((jumpValueLower === 'no' || jumpValueLower === 'nein') && formDataValue === false)
          )
        }
        return jump.value === formData.value[currentField.uuid]
      })
      if (jumpCondition) {
        if (jumpCondition.uuid === 'END') {
          return flatFields.value.length // Jump to the last field
        }
        const jumpIndex = flatFields.value.findIndex((field) => field.uuid === jumpCondition.uuid)
        if (jumpIndex !== -1) {
          return jumpIndex
        }
      }
    }
    return currentIndex + 1
  }

  function getFieldByUuid(uuid: string) {
    return flatFields.value.find((field) => field.uuid === uuid)
  }

  function getFieldSectionMapping(fieldUuid?: string): FieldSectionMapping | undefined {
    return fieldSectionMap.value.get(fieldUuid ?? activeFieldId.value)
  }

  // Count repetitions for a section by tracking original_section_uuid
  function countRepetitionsForSection(section: QuestionnaireSection): number {
    const sectionAny = section as any
    // Get the original UUID - either from original_section_uuid (if this is a copy) or the section's own UUID (if this is the original)
    const originalUuid = sectionAny.original_section_uuid || section.uuid

    return rawSchema.value.filter((s) => {
      const sAny = s as any
      // Match if: this IS the original, OR it was copied from the original
      return s.uuid === originalUuid || sAny.original_section_uuid === originalUuid
    }).length
  }

  // Check if we can add more repetitions for a section
  function canAddMoreRepetitions(sectionInfo: SectionInfo): boolean {
    const maxRepetitions = (sectionInfo.section as any).max_repetitions
    if (maxRepetitions === null || maxRepetitions === undefined) {
      return true // No limit set
    }
    const currentCount = countRepetitionsForSection(sectionInfo.section)
    return currentCount <= maxRepetitions
  }

  const visibleFieldUuids = computed(() => {
    const visible = new Set<string>()
    const visited = new Set<number>()
    let currentIndex = 0

    while (currentIndex < flatFields.value.length) {
      if (visited.has(currentIndex)) break
      visited.add(currentIndex)
      visible.add(flatFields.value[currentIndex].uuid)
      currentIndex = findNextField(currentIndex)
    }

    return visible
  })

  // Check if the current field is the last field in a repeatable section
  const isLastFieldOfRepeatableSection = computed(() => {
    const mapping = fieldSectionMap.value.get(activeFieldId.value)
    if (!mapping) return false
    return mapping.isLastFieldInSection && mapping.sectionInfo.isRepeatable
  })

  // Get current section info for repeat prompt
  const currentRepeatableSectionInfo = computed(() => {
    const mapping = fieldSectionMap.value.get(activeFieldId.value)
    if (!mapping || !mapping.sectionInfo.isRepeatable) return null
    return mapping.sectionInfo
  })

  function getRawSchema() {
    return rawSchema.value
  }

  function setRawSchema(schema: QuestionnaireSection[]) {
    rawSchema.value = schema
  }

  return {
    formData,
    activeFieldId,
    activeFieldIndex,
    flatFields,
    rawSchema,
    formLength,
    isLastField,
    isFirstField,
    isLastFieldOfRepeatableSection,
    currentRepeatableSectionInfo,
    showRepeatSectionPrompt,
    pendingRepeatSectionInfo,
    visibleFieldUuids,
    updateFormData,
    setFormData,
    initializeForm,
    jumpToField,
    goToNextField,
    goToPreviousField,
    setRepeatSectionPrompt,
    getFieldByUuid,
    getFieldSectionMapping,
    getRawSchema,
    setRawSchema,
    canAddMoreRepetitions
  }
})
